import omit from 'lodash/omit';

import { isBookingProcessAlias } from '../../transactions/transaction';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { storableError } from '../../util/errors';
import { uniqueBy } from '../../util/generators';
import * as log from '../../util/log';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  fetchStripeAccount,
  updateStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser, fetchCurrentUserHasListingsSuccess } from '../../ducks/user.duck';
import { fetchCategoryRows } from '../../hooks/useCategoryConfigMap';
import {
  createAlgoliaObject,
  apiBaseUrl,
  onGetDealsCategoriesList,
  onGetStockImages,
  onUpdateBusinessProfile,
  onUpdateCategoriesProfile,
  onUpdateDealCategoriesProfile,
} from '../../util/api';

const { UUID } = sdkTypes;

import moment from 'moment';
import {
  BUSINESS_PROFILE_LISTING_TYPE,
  DEAL_LISTING_TYPE,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PUBLISHED,
  PRODUCT_LISTING_TYPE,
  SERVICE_LISTING_TYPE,
  STAFF_LISTING_TYPE,
} from '../../util/types';
import { createListing } from '../MerchantOnboardingPage/MerchantOnboardingPage.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { TEMPLATE_IMAGE_FETCH_LIMIT } from './EditListingWizard/templateImageHelpers';
import { Events, mixPanelEventsData, mixPanelEventsScreens } from '../../mixpanel';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
      uploadedImages: {},
      uploadedImagesOrder: [],
    }
    : {
      uploadedImages,
      uploadedImagesOrder,
    };
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (weeklyExceptionQueries, weekStartId, newDataProps) => {
  return weekStartId
    ? {
      weeklyExceptionQueries: {
        ...weeklyExceptionQueries,
        [weekStartId]: {
          ...weeklyExceptionQueries[weekStartId],
          ...newDataProps,
        },
      },
    }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (monthlyExceptionQueries, monthId, newDataProps) => {
  return monthId
    ? {
      monthlyExceptionQueries: {
        ...monthlyExceptionQueries,
        [monthId]: {
          ...monthlyExceptionQueries[monthId],
          ...newDataProps,
        },
      },
    }
    : {};
};

function addValue(number) {
  // Determine the value to add based on the number's range
  if (number > 0 && number <= 5) {
    return 5;
  } else if (number > 5 && number <= 10) {
    return 10;
  } else if (number > 10 && number <= 20) {
    return 20;
  } else if (number > 20 && number <= 30) {
    return 30;
  } else if (number > 30 && number <= 50) {
    return 50;
  } else if (number > 50 && number <= 100) {
    return 100;
  }
}

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/EditListingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/EditListingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/EditListingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';
export const FETCH_STOCK_IMAGES_REQUEST = 'app/EditListingPage/FETCH_STOCK_IMAGES_REQUEST';
export const FETCH_STOCK_IMAGES_SUCCESS = 'app/EditListingPage/FETCH_STOCK_IMAGES_SUCCESS';
export const FETCH_STOCK_IMAGES_ERROR = 'app/EditListingPage/FETCH_STOCK_IMAGES_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const UPDATE_BUSINESS_PROFILE_REQUEST =
  'app/EditListingPage/UPDATE_BUSINESS_PROFILE_REQUEST';
export const UPDATE_BUSINESS_PROFILE_SUCCESS =
  'app/EditListingPage/UPDATE_BUSINESS_PROFILE_REQUEST';
export const UPDATE_BUSINESS_PROFILE_ERROR = 'app/EditListingPage/UPDATE_BUSINESS_PROFILE_REQUEST';

export const DEALS_CATEGORIES_REQUEST = 'app/EditListingPage/DEALS_CATEGORIES_REQUEST';
export const DEALS_CATEGORIES_SUCCESS = 'app/EditListingPage/DEALS_CATEGORIES_SUCCESS';
export const DEALS_CATEGORIES_ERROR = 'app/EditListingPage/DEALS_CATEGORIES_ERROR';

export const CATEGORIES_REQUEST = 'app/EditListingPage/CATEGORIES_REQUEST';
export const CATEGORIES_SUCCESS = 'app/EditListingPage/CATEGORIES_SUCCESS';
export const CATEGORIES_ERROR = 'app/EditListingPage/CATEGORIES_ERROR';

export const DEAL_ITEMS_UPDATE_REQUEST = 'app/EditListingPage/DEAL_ITEMS_UPDATE_REQUEST';
export const DEAL_ITEMS_UPDATE_SUCCESS = 'app/EditListingPage/DEAL_ITEMS_UPDATE_SUCCESS';
export const DEAL_ITEMS_UPDATE_ERROR = 'app/EditListingPage/DEAL_ITEMS_UPDATE_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  stockImages: [],
  stockImagesFetchInProgress: false,
  stockImagesError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  businessProfileRequest: false,
  businessProfileSuccess: null,
  businessProfileError: null,
  dealsCategoriesRequest: false,
  dealsCategoriesSuccess: [],
  dealsCategoriesError: null,
  categoriesRequest: false,
  categoriesSuccess: [],
  categoriesError: null,
  dealItemsUpdateRequest: false
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const { listingId, allExceptions, weeklyExceptionQueries, monthlyExceptionQueries } = state;
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
          ...initialState,
          listingId,
          allExceptions,
          weeklyExceptionQueries,
          monthlyExceptionQueries,
        }
        : { ...initialState, listingId: listingIdFromPayload };
    }
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = { fetchExceptionsError: null, fetchExceptionsInProgress: true };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = { fetchExceptionsInProgress: false, fetchExceptionsError: error };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(state.monthlyExceptionQueries, monthId, newData)
        : weekStartId
          ? mergeToWeeklyExceptionQueries(state.weeklyExceptionQueries, weekStartId, newData)
          : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(sortExceptionsByStartTime);
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const exception = payload;
      const id = exception.id.uuid;
      const allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case FETCH_STOCK_IMAGES_REQUEST:
      return { ...state, stockImagesFetchInProgress: true, stockImagesError: null };
    case FETCH_STOCK_IMAGES_SUCCESS: {
      const stockImages = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      return {
        ...state,
        stockImagesFetchInProgress: false,
        stockImages,
      };
    }
    case FETCH_STOCK_IMAGES_ERROR:
      return { ...state, stockImagesFetchInProgress: false, stockImagesError: payload };

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case UPDATE_BUSINESS_PROFILE_REQUEST:
      return {
        ...state,
        businessProfileRequest: true,
      };

    case UPDATE_BUSINESS_PROFILE_SUCCESS:
      return {
        ...state,
        businessProfileRequest: false,
        businessProfileSuccess: payload,
      };

    case UPDATE_BUSINESS_PROFILE_ERROR:
      return {
        ...state,
        businessProfileError: payload,
      };

    case DEALS_CATEGORIES_REQUEST:
      return {
        ...state,
        dealsCategoriesRequest: true,
        dealsCategoriesSuccess: [],
      };

    case DEALS_CATEGORIES_SUCCESS:
      return {
        ...state,
        dealsCategoriesRequest: false,
        dealsCategoriesSuccess: payload,
      };

    case DEALS_CATEGORIES_ERROR:
      return {
        ...state,
        dealsCategoriesError: payload,
      };

    case CATEGORIES_REQUEST:
      return {
        ...state,
        categoriesRequest: true,
      };

    case CATEGORIES_SUCCESS:
      return {
        ...state,
        categoriesRequest: false,
        categoriesSuccess: payload,
      };

    case CATEGORIES_ERROR:
      return {
        ...state,
        categoriesError: payload,
      };

    case DEAL_ITEMS_UPDATE_REQUEST:
      return {
        ...state,
        dealItemsUpdateRequest: true,
      };

    case DEAL_ITEMS_UPDATE_SUCCESS:
      return {
        ...state,
        dealItemsUpdateRequest: false,
        // dealItemsUpdateSuccess: payload,
      };

    case DEAL_ITEMS_UPDATE_ERROR:
      return {
        ...state,
        dealItemsUpdateRequest: false
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const categoriesRequestFn = () => ({
  type: DEALS_CATEGORIES_REQUEST,
});

export const categoriesSuccessFn = payload => ({
  type: DEALS_CATEGORIES_SUCCESS,
  payload: payload,
});

export const categoriesErrorFn = response => ({
  type: DEALS_CATEGORIES_ERROR,
  payload: response,
});
export const dealItemsUpdateRequestFn = () => ({
  type: DEAL_ITEMS_UPDATE_REQUEST,
});

export const dealItemsUpdateSuccessFn = () => ({
  type: DEAL_ITEMS_UPDATE_SUCCESS,
  // payload: payload,
});

export const dealItemsUpdateErrorFn = () => ({
  type: DEAL_ITEMS_UPDATE_ERROR,
  // payload: response,
});

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

const updateBusinessProfileRequestFn = () => ({
  type: UPDATE_BUSINESS_PROFILE_REQUEST,
});

const updateBusinessProfileSuccessFn = payload => ({
  type: UPDATE_BUSINESS_PROFILE_SUCCESS,
  payload,
});

const updateBusinessProfileErrorFn = payload => ({
  type: UPDATE_BUSINESS_PROFILE_ERROR,
  payload,
});

export const categoriesRequest = () => ({
  type: CATEGORIES_REQUEST,
});

export const categoriesSuccess = payload => ({
  type: CATEGORIES_SUCCESS,
  payload: payload,
});

export const categoriesError = response => ({
  type: CATEGORIES_ERROR,
  payload: response,
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);
export const fetchStockImagesRequest = requestAction(FETCH_STOCK_IMAGES_REQUEST);
export const fetchStockImagesSuccess = successAction(FETCH_STOCK_IMAGES_SUCCESS);
export const fetchStockImagesError = errorAction(FETCH_STOCK_IMAGES_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

// ================ Thunk ================ //

// fetchCategoryRows rather than a raw onGetCategories() call: by the time a
// merchant reaches this wizard for a service, NewListingCategoryPage has
// already resolved the same request and cached the promise. Calling the API
// directly here duplicated that fetch and, since loadData chains this behind
// fetchCurrentUser, left the details tab showing an empty placeholder for two
// network round trips before the category card had anything to render.
export const categoriesListingFn = () => async dispatch => {
  dispatch(categoriesRequest);
  return fetchCategoryRows()
    .then(response => {
      dispatch(categoriesSuccess(response));
    })
    .catch(error => {
      dispatch(categoriesError(error.message));
    });
};

export const categoriesListing = () => async dispatch => {
  dispatch(categoriesRequestFn);
  try {
    const response = await onGetDealsCategoriesList();
    dispatch(categoriesSuccessFn(response));
    return response;
  } catch (error) {
    dispatch(categoriesErrorFn(error.message));
  }
};

export function requestShowListing(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(showListingsRequest(actionPayload));
    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        dispatch(categoriesListingFn());
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

export const updateBusinessProfile = body => async dispatch => {
  dispatch(updateBusinessProfileRequestFn());
  try {
    const response = await onUpdateBusinessProfile(body);
    dispatch(updateBusinessProfileSuccessFn(response));
  } catch (error) {
    dispatch(updateBusinessProfileErrorFn(error.message));
  }
};

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestCreateListingDraft(data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraftRequest(data));
    const { stockUpdate, images, publicData, ...rest } = data;
    const { deal_start_date, deal_end_date } = publicData || {};

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    // createDraft is the one call every listing type (product/service/deal/
    // business-profile/staff) goes through exactly once, on its first save —
    // see onCompleteEditListingWizardTab in EditListingWizardTab.js, which
    // only routes here while the listing has no id yet. Stamping platform
    // here means it's set once, at creation, and every later tab save (which
    // goes through requestUpdateListing instead) leaves it untouched.
    const ownListingValues = {
      ...imageProperty,
      publicData: { ...publicData, platform: 'web' },
      ...rest,
    };

    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    let createDraftResponse = null;
    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        const listing = response.data.data;
        const { listingType } = (listing && listing.id && listing.attributes.publicData) || {};

        if (listingType == 'business-profile') {
          dispatch(fetchCurrentUserHasListingsSuccess(true, response.data.data));
        }
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

// Create or Update listing in Algolia
export const createListingInAlgolia = (response, currentUser = {}) => {
  const {
    title = '',
    description = '',
    price,
    geolocation,
    createdAt,
    publicData = {},
    metaData = {},
    availabilityPlan,
    state,
  } = response?.data?.data?.attributes || {};

  // if (state == "published") {
  const listingType = publicData?.listingType;
  const algoliaIndex = () => {
    switch (listingType) {
      case BUSINESS_PROFILE_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_BUSINESS_INDEX;
      case DEAL_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_DEALS_INDEX;
      case PRODUCT_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_PRODUCTS_INDEX;
      case SERVICE_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_SERVICE_INDEX;
      case STAFF_LISTING_TYPE:
        return process.env.REACT_APP_ALGOLIA_STAFF_INDEX;
      default:
        return '';
    }
  };

  const includedData = response?.data?.included || [];
  const images = includedData
    .filter(data => data?.type === 'image')
    .map(data => data?.attributes?.variants?.['listing-card-2x']?.url);

  const { email, profile } = currentUser?.attributes || {};

  const { firstName, lastName, displayName, publicData: currentUserPublicData } = profile || {};

  const currentUserPublicDatMaybe = !!currentUserPublicData
    ? { publicData: { ...currentUserPublicData } }
    : {};

  const geolocationMaybe = geolocation ? { _geoloc: { lat: geolocation?.lat, lng: geolocation?.lng } } : {};

  createAlgoliaObject({
    indexName: algoliaIndex(),
    objectID: response?.data?.data?.id?.uuid,
    title,
    description,
    price,
    images,
    ...geolocationMaybe,
    publicData: { ...publicData },
    metaData,
    createdAt,
    author: {
      id: currentUser?.id?.uuid,
      firstName,
      lastName,
      email,
      displayName,
      ...currentUserPublicDatMaybe,
    },
    availabilityPlan,
    state,
  });
  // }
};

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
export function requestUpdateListing(tab, data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListingRequest(data));
    const { id, stockUpdate, images, publicData, ...rest } = data;

    const state = getState();
    const { currentUser, currentUserListing } = state?.user || {};
    const { profileListingId = '' } = currentUser?.attributes?.profile?.publicData || {};
    const businessListingIdMaybe = profileListingId ? { profileListingId } : {};
    const listingIdUuid = id?.uuid || id;
    const existingListingPublicData =
      state.marketplaceData?.entities?.ownListing?.[listingIdUuid]?.attributes?.publicData || {};


    const { geolocation: businessGeolocation, publicData: businessPublicData } = currentUserListing?.attributes || {};
    const { location: businessLocation } = businessPublicData || {};

    // Prefer the geolocation/location coming from this update; only fall back to the
    // previously cached business geolocation/location when this update doesn't include one
    // (e.g. saving availability alone shouldn't wipe out a previously set location).
    const geolocationMaybe = rest.geolocation
      ? { geolocation: rest.geolocation }
      : businessGeolocation
        ? { geolocation: businessGeolocation }
        : {};
    const locationMaybe = publicData?.location
      ? { location: publicData.location }
      : businessLocation
        ? { location: businessLocation }
        : {};

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = {
      id,
      ...imageProperty,
      ...rest,
      ...geolocationMaybe,
      publicData: {
        ...existingListingPublicData,
        ...publicData,
        pickupEnabled: true,
        shippingEnabled: true,
        ...(publicData?.listingType !== BUSINESS_PROFILE_LISTING_TYPE ? businessListingIdMaybe : {}),
        ...(publicData?.listingType !== BUSINESS_PROFILE_LISTING_TYPE ? locationMaybe : {}),
      },
    };
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    const existingTimeZone =
      state.marketplaceData.entities.ownListing[id.uuid]?.attributes?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(markTabUpdated(tab));

        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(dispatch, listing, searchString, firstDayOfWeek);
        }

        return response;
      })
      .then(response => {
        // Extract listing state and listing data from the response
        const listingData = response?.data?.data;
        const listingId = listingData?.id?.uuid;
        const { state: listingState, availabilityPlan = {} } = listingData?.attributes || {};
        const { listingType, categoryLevel2, deal_start_date, deal_end_date, availabilityExceptionId } =
          (listingData && listingData.id && listingData.attributes.publicData) || {};

        const categoryUpdateObj = {
          categoryId: categoryLevel2,
          businessListingId: profileListingId,
          operation: 'add',
        };

        if (listingType === DEAL_LISTING_TYPE && availabilityExceptionId) {
          // Delete old Availability exception
          dispatch(requestDeleteAvailabilityException({ id: availabilityExceptionId }))
            .then(() => {
              // Create new Availability exception
              const startDateInMs = deal_start_date * 1000;
              const endDateInMs = deal_end_date * 1000;
              const startMomentDate = moment.tz(startDateInMs, availabilityPlan?.timezone);
              const endMomentDate = moment.tz(endDateInMs, availabilityPlan?.timezone);
              const startOfDay = startMomentDate.startOf('day');
              const endOfDay = endMomentDate.startOf('day');
              const seats = 100;

              const range = {
                start: startOfDay.toISOString(),
                end: endOfDay.toISOString(),
              };

              const params = {
                listingId,
                seats,
                ...range,
              };

              dispatch(requestAddAvailabilityException(params))
                .then((data) => {
                  console.log("created exception on update deal", data);
                  const availabilityExceptionId = data && data?.id?.uuid;
                  if (availabilityExceptionId) {
                    dispatch(updateListing({ id: listingId, publicData: { availabilityExceptionId } }, config));
                  }
                })
                .catch(e => {
                  console.error('Failed to create availability exception on deal update:', e);
                })
            })

          onUpdateDealCategoriesProfile(categoryUpdateObj);
        }
        else if (listingType === PRODUCT_LISTING_TYPE || listingType === SERVICE_LISTING_TYPE) {
          onUpdateCategoriesProfile(categoryUpdateObj);
        }
        // Check if the listing is a business profile and dispatch success action
        else if (listingType === BUSINESS_PROFILE_LISTING_TYPE) {
          dispatch(fetchCurrentUserHasListingsSuccess(true, listingData));
        }

        // If the listing is published, create it in Algolia
        if (listingState !== LISTING_STATE_DRAFT) {
          // createListingInAlgolia(response, currentUser);
        }

        // Return the original response for further use
        return response;
      })

      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export function updateListing(data, config) {
  return (dispatch, getState, sdk) => {
    // dispatch(updateListingRequest(data));
    const { id, stockUpdate, images, publicData, ...rest } = data;

    const state = getState();
    const currentUser = state?.user?.currentUser || {};
    const { profileListingId = '' } = currentUser?.attributes?.profile?.publicData || {};
    const businessListingIdMaybe = profileListingId ? { profileListingId } : {};

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = {
      id,
      ...imageProperty,
      ...rest,
      publicData: {
        ...publicData,
        ...businessListingIdMaybe,
        pickupEnabled: true,
        shippingEnabled: true,
      },
    };
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));

        // createListingInAlgolia(response, currentUser);
        return response;
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export const requestUpdateDealItems = (percentageDiscountValue, listId) => async (dispatch, getState, sdk) => {
  dispatch(dealItemsUpdateRequestFn(percentageDiscountValue, listId))
  // const {percentageDiscountValue,listId}= params;
  const listingId = new UUID(listId);
  // console.log('listingId', listingId)
  try {
    const listResponse = await sdk.ownListings.show({ id: listingId });
    let percentage_discount_value = listResponse?.data?.data.attributes?.publicData?.percentage_discounts ?? 0;
    percentage_discount_value = percentageDiscountValue > percentage_discount_value ? percentageDiscountValue : percentage_discount_value;
    const response = await sdk.ownListings.update({
      id: listingId,
      publicData: {
        percentage_discounts: addValue(percentage_discount_value),
      },
    }, {
      expand: true,
      include: ['images']
    });
    // const state = getState();
    // await createListingInAlgolia(response, state?.user?.currentUser);
    dispatch(dealItemsUpdateSuccessFn())
    return;
  } catch (err) {
    dispatch(dealItemsUpdateErrorFn());
    console.log(err);
  }
}

export const requestPublishListingDraft = (listingId, config) => (dispatch, getState, sdk) => {
  dispatch(publishListingRequest(listingId));

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const queryParams = {
    expand: true,
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
  };

  return sdk.ownListings
    .publishDraft({ id: listingId }, queryParams)
    .then(response => {
      const state = getState();
      // createListingInAlgolia(response, state?.user?.currentUser);
      const profileListingId =
        state.user?.currentUser?.attributes?.profile?.publicData?.profileListingId;

      // Add the created listing to the marketplace data
      const listing = response.data.data;
      const listingId = listing?.id?.uuid;
      const { listingType, categoryLevel2, deal_start_date, deal_end_date } =
        (listing && listing.id && listing.attributes.publicData) || {};

      // DEBUG: confirm what was published. A deal only shows in search + counts when
      // state === 'published'; listingsCategories must hold the linked service's subcategory id.
      console.log('🟢 [LISTING PUBLISH] published in Sharetribe:', {
        listingId,
        listingType,
        state: listing?.attributes?.state,
        categoryLevel2,
        listingsCategories: listing?.attributes?.publicData?.listingsCategories,
        title: listing?.attributes?.title,
      });

      // Index the published listing directly in Algolia. The background event poller is the primary
      // indexer in production but is disabled in local dev (server/apiServer.js), so without this a
      // locally-published deal never reaches the deals index. createAlgoliaObject posts to
      // /algolia/createObject (runs locally); saveObject is idempotent so it's safe alongside the poller.
      createListingInAlgolia(response, state?.user?.currentUser);

      const availabilityPlan = listing?.attributes?.availabilityPlan || {};
      if (listingType == BUSINESS_PROFILE_LISTING_TYPE) {
        dispatch(fetchCurrentUserHasListingsSuccess(true, response.data.data));
      }

      const categoryUpdateObj = {
        categoryId: categoryLevel2,
        businessListingId: profileListingId,
        operation: 'add',
      };

      // Dispatch actions to update categories in the user's profile
      if (listingType === DEAL_LISTING_TYPE) {
        // Create Availability exception for Deal
        const startDateInMs = deal_start_date * 1000;
        const endDateInMs = deal_end_date * 1000;
        const startMomentDate = moment.tz(startDateInMs, availabilityPlan?.timezone);
        const endMomentDate = moment.tz(endDateInMs, availabilityPlan?.timezone);
        const startOfDay = startMomentDate.startOf('day');
        const endOfDay = endMomentDate.startOf('day');
        const seats = 100;

        const range = {
          start: startOfDay.toISOString(),
          end: endOfDay.toISOString(),
        };
        const params = {
          listingId,
          seats,
          ...range,
        };

        dispatch(requestAddAvailabilityException(params))
          .then((data) => {
            console.log("created exception on publish", data);
            const availabilityExceptionId = data && data?.id?.uuid;
            if (availabilityExceptionId) {
              dispatch(updateListing({ id: listingId, publicData: { availabilityExceptionId } }, config));
            }
          })
          .catch(e => {
            console.error('Failed to create availability exception on deal publish:', e);
          })

        onUpdateDealCategoriesProfile(categoryUpdateObj);
      }
      else if (listingType === PRODUCT_LISTING_TYPE) {
        onUpdateCategoriesProfile(categoryUpdateObj);
      }
      else if (listingType === STAFF_LISTING_TYPE) {
        dispatch(
          updateBusinessProfile({
            staffId: response.data.data?.id?.uuid,
            id: profileListingId,
          })
        );
      }
      else if (listingType === SERVICE_LISTING_TYPE) {
        const serviceId = listing?.id?.uuid;
        const serviceTitle = listing?.attributes?.title;
        const { currentUser, currentUserListing, currentUserStaffListing } = state?.user || {};

        const { serviceIds = [], services = [] } = currentUserStaffListing?.attributes?.publicData || {};

        const { defaultStaffId } = currentUser?.attributes?.profile?.publicData || {};
        const profileListingId = currentUserListing?.id?.uuid;
        const { availabilityPlan, title } = currentUserListing?.attributes || {};

        const { email: merchantEmail, profile } = currentUser?.attributes || {};
        const { protectedData } = profile || {};
        const { mobile_phone_number, phoneNumber } = protectedData || {};

        if (defaultStaffId) {
          const staffDetails = {
            id: defaultStaffId,
            title,
            publicData: {
              serviceIds: [...serviceIds, serviceId],
              services: [
                ...services,
                {
                  key: serviceId,
                  label: serviceTitle,
                },
              ]
            }
          }

          dispatch(requestUpdateListing("", staffDetails, config))
            .then(res => console.log(res))
            .catch(err => console.log(err))
        }
        else if (availabilityPlan && serviceId) {
          const staffDetails = {
            title: title || "Staff",
            publicData: {
              email: merchantEmail,
              mobile_phone_number: mobile_phone_number,
              phoneNumber: phoneNumber,
              profileListingId,
              serviceIds: [serviceId],
              services: [{
                key: serviceId,
                label: serviceTitle,
              }],
              isDefault: true,
              listingType: STAFF_LISTING_TYPE,
              transactionProcessAlias: "default-booking/release-1",
              unitType: "hour",
            },
            availabilityPlan,
          };

          dispatch(createListing(staffDetails, config))
            .then(res => {
              const staffId = res?.data?.data?.id?.uuid;
              dispatch(updateProfile({ publicData: { defaultStaffId: staffId } }));
            })
        }
        onUpdateCategoriesProfile(categoryUpdateObj);
      }

      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .then(response => {
      const state = getState();
      const { currentUser } = state?.user || {};

      const listing = response.data.data;
      const { listingType } = (listing && listing?.id && listing?.attributes?.publicData) || {};

      // Map listingType → event name
      const EVENT_MAP = {
        [SERVICE_LISTING_TYPE]: Events.MERCHANT_SERVICE_CREATED,
        [PRODUCT_LISTING_TYPE]: Events.MERCHANT_PRODUCT_CREATED,
        [DEAL_LISTING_TYPE]: Events.MERCHANT_DEAL_CREATED,
      };

      // Map listingType → action type
      const ACTION_TYPE_MAP = {
        [SERVICE_LISTING_TYPE]: mixPanelEventsData.CREATE_SERVICE,
        [PRODUCT_LISTING_TYPE]: mixPanelEventsData.CREATE_PRODUCT,
        [DEAL_LISTING_TYPE]: mixPanelEventsData.CREATE_DEAL,
      };

      const eventName = EVENT_MAP[listingType] || null;
      const actionType = ACTION_TYPE_MAP[listingType] || null;

      if (eventName && actionType) {
        const attributes = listing?.attributes || {};
        const publicData = attributes?.publicData || {};
        const availabilityPlan = attributes?.availabilityPlan;

        // Default payload for all listing types
        const payload = {
          action_type: actionType,
          screen: mixPanelEventsScreens.EDIT_LISTING,
          user_type: 'provider',
          user_id: currentUser?.id?.uuid,
        };

        // Add extra deal-specific fields if this is a deal listing
        if (listingType === DEAL_LISTING_TYPE && availabilityPlan?.timezone) {
          const timezone = availabilityPlan?.timezone;
          const startEpochSec = publicData?.deal_start_date; // expected epoch seconds
          const endEpochSec = publicData?.deal_end_date; // expected epoch seconds

          // Convert to milliseconds if provided as seconds, then format in store timezone when available
          const startMs = typeof startEpochSec === 'number' ? startEpochSec * 1000 : undefined;
          const endMs = typeof endEpochSec === 'number' ? endEpochSec * 1000 : undefined;

          const startFormatted = startMs
            ? (timezone ? moment.tz(startMs, timezone) : moment(startMs)).format('YYYY-MM-DD')
            : undefined;
          const endFormatted = endMs
            ? (timezone ? moment.tz(endMs, timezone) : moment(endMs)).format('YYYY-MM-DD')
            : undefined;

          payload.deal_id = listing?.id?.uuid;
          payload.deal_title = attributes?.title;
          if (startFormatted) payload.deal_start_date = startFormatted;
          if (endFormatted) payload.deal_end_date = endFormatted;
        }

        trackEventsMixpanel(eventName, payload);
      }

      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload, listingImageConfig) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        const uploadedImage = { ...img, id, imageId: img.id, file: actionPayload.file };
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        dispatch(uploadImageSuccess({ data: uploadedImage }));
        return uploadedImage;
      })
      .catch(e => {
        const error = storableError(e);
        dispatch(uploadImageError({ id, error }));
        throw error;
      });
  };
}

export const requestFetchStockImages =
  ({ categoryId, subcategoryId, limit = TEMPLATE_IMAGE_FETCH_LIMIT }) =>
  dispatch => {
    if (!categoryId || !subcategoryId) {
      return dispatch(fetchStockImagesSuccess({ data: [] }));
    }

    dispatch(
      fetchStockImagesRequest({
        categoryId,
        subcategoryId,
        limit,
      })
    );

    return onGetStockImages(categoryId, subcategoryId, { limit })
      .then(response => dispatch(fetchStockImagesSuccess(response)))
      .catch(error => dispatch(fetchStockImagesError(storableError(error))));
  };

export const requestUseTemplateImage =
  ({ templateImage, listingImageConfig }) =>
  dispatch => {
    const filePath = `/api/image-library/stock-images/${templateImage.id}/file`;
    const fileUrl = `${apiBaseUrl()}${filePath}`;

    return window
      .fetch(fileUrl, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(text || 'download_failed');
          });
        }
        return response.blob();
      })
      .then(blob => {
        const contentType = blob.type || 'image/jpeg';
        const ext =
          contentType === 'image/png'
            ? 'png'
            : contentType === 'image/webp'
              ? 'webp'
              : 'jpg';
        const file = new File([blob], `template_${templateImage.id}.${ext}`, { type: contentType });

        return dispatch(
          requestImageUpload(
            { id: `template_${templateImage.id}_${Date.now()}`, file },
            listingImageConfig
          )
        );
      });
  };

export const requestAddAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(addAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      dispatch(addAvailabilityExceptionSuccess({ data: availabilityException }));
      return availabilityException;
    })
    .catch(e => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestDeleteAvailabilityException = params => (dispatch, getState, sdk) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(deleteAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      // In theory, there could be several pagination pages worth of exceptions,
      // if range is month and unit is 'hour': 31 days * 24 hour = 744 slots for exceptions.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        // It's unlikely that this code is reached with default units.
        // Note:
        //  - Firing multiple API calls might hit API rate limit
        //    (This is very unlikely with this query and 'hour' unit.)
        //  - TODO: this doesn't take care of failures of those extra calls
        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) => all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsSuccess({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch(e => {
      return dispatch(
        fetchAvailabilityExceptionsError({ ...timeUnitIdProp, error: storableError(e) })
      );
    });
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone = listing.attributes.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(todayInListingsTZ, 'month', timezone, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', timezone, 1, 'months');

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};
// export const showBussinessDetails = (params) => (dispatch, getState, sdk) => {
//   sdk.ownListings.query;
// };

// Upload file in Cloudinary
export const fileUploadCloudinary = file => (dispatch, getState, sdk) => {
  const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
  const formData = new FormData();
  const fileType = file.type;

  formData.append('file', file);
  formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
  formData.append('resource_type', fileType);
  formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);

  return fetch(uploadURL, { method: 'POST', body: formData })
    .then(response => response.text())
    .then(data => JSON.parse(data))
    .catch(e => {
      console.log(e);
      return {};
    });
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  const { id, type } = params;

  if (type === 'new' || type === 'product' || type === 'service') {
    // No need to listing data when creating a new listing
    // categoriesListingFn doesn't read anything fetchCurrentUser produces, but
    // was chained behind it with a plain .then() — so the category card on
    // this tab sat blank for the length of an unrelated currentUser round
    // trip even after fetchCategoryRows() had a cached response ready to
    // serve instantly. Dispatched alongside it instead so it isn't waiting on
    // a request it has no dependency on.
    return Promise.all([dispatch(fetchCurrentUser()), dispatch(categoriesListingFn())])
      .then(([response]) => {
        const { profileListingId } = response?.data?.data.attributes.profile.publicData || '';
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
          dispatch(showBussinessDetails(profileListingId));
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([dispatch(requestShowListing(payload, config)), dispatch(fetchCurrentUser())])
    .then(response => {
      const currentUser = getState().user.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }

      // Because of two dispatch functions, response is an array.
      // We are only interested in the response from requestShowListing here,
      // so we need to pick the first one
      const listing = response[0]?.data?.data;
      const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
      if (listing && isBookingProcessAlias(transactionProcessAlias)) {
        fetchLoadDataExceptions(dispatch, listing, search, config.localization.firstDayOfWeek);
      }

      dispatch(categoriesListing());
      dispatch(categoriesListingFn());
      return response;
    })
    .catch(e => {
      throw e;
    });
};
